/* ─── NoCosign: swipeable media strip on product cards (mobile only) ───

   Progressive enhancement over assets/component-card-product-swipe.css. The
   paging is done entirely by CSS scroll-snap; this element has two jobs:

     1. move the active dot as the strip scrolls, and
     2. stop a swipe from being treated as a tap on the product link that
        wraps the strip.

   It is a custom element rather than a one-shot init pass so that cards
   injected after page load — facet filtering, the quick-cart drawer, the
   card-product-slider fetch — upgrade on insertion with no extra wiring.

   No Swiper instance is created here on purpose: a collection grid can hold
   dozens of cards, and dozens of Swiper instances is a great deal of work
   for a control that a native scroll container already provides. */

if (!customElements.get('card-media-swipe')) {
  /* One resize listener for the whole page rather than one per card. */
  const instances = new Set();
  let resizeBound = false;

  const onResize = () => {
    instances.forEach((instance) => instance.update());
  };

  class CardMediaSwipe extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.track = this.querySelector('[data-card-swipe-track]');
      this.dots = Array.from(this.querySelectorAll('[data-card-swipe-dot]'));

      /* Liquid only emits this element for products with more than one media
         item, but never assume the markup. */
      if (!this.track || this.dots.length < 2) return;

      this.initialised = true;
      this.index = 0;
      this.ticking = false;
      this.startX = null;
      this.startY = null;
      this.startScrollLeft = null;

      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      /* Capture phase: the decision has to be made before the click reaches
         the <a class="product-card__link"> this element sits inside. */
      this.addEventListener('click', this.onClickCapture, true);

      instances.add(this);

      if (!resizeBound) {
        resizeBound = true;
        window.addEventListener('resize', onResize, { passive: true });
      }
    }

    disconnectedCallback() {
      if (!this.initialised) return;

      this.track.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('click', this.onClickCapture, true);
      instances.delete(this);
      this.initialised = false;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      const width = this.track.clientWidth;
      /* Zero on desktop, where the element is display:none — nothing to do. */
      if (!width) return;

      /* Math.abs: in RTL locales scrollLeft counts down from zero. */
      const raw = Math.round(Math.abs(this.track.scrollLeft) / width);
      const next = Math.max(0, Math.min(this.dots.length - 1, raw));
      if (next === this.index) return;

      this.dots[this.index].classList.remove('is-active');
      this.dots[next].classList.add('is-active');
      this.index = next;
    }

    onPointerDown(event) {
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startScrollLeft = this.track.scrollLeft;
    }

    /* Tap versus swipe. A tap opens the product; a swipe must not. Two
       independent signals, either of which is enough to call it a swipe:
       the strip actually moved, or the pointer travelled far enough that the
       shopper clearly meant to drag. A click with no preceding pointerdown
       (keyboard, assistive tech) is always allowed through. */
    onClickCapture(event) {
      if (this.startX === null) return;

      const moved = Math.abs(this.track.scrollLeft - this.startScrollLeft) > 2;
      const dragged =
        Math.abs(event.clientX - this.startX) > 10 ||
        Math.abs(event.clientY - this.startY) > 10;

      this.startX = null;
      this.startY = null;
      this.startScrollLeft = null;

      if (moved || dragged) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  customElements.define('card-media-swipe', CardMediaSwipe);
}
